import { paymentMethodEnum } from "./payment";
import { lineItemSchema } from "./document";
import { z } from "zod";

export const expenseStatusEnum = z.enum(["draft", "pending", "partially-paid", "paid", "overdue"]);
export type ExpenseStatusType = z.infer<typeof expenseStatusEnum>;

// Expense-specific schema (expenses don't use line items, taxes array, or showItemsWithTax)
export const baseExpenseSchema = z.object({
  // Organization Details
  organizationName: z.string(),
  organizationAddressLine1: z.string().optional().nullable(),
  organizationAddressLine2: z.string().optional().nullable(),
  organizationZipcode: z.string().optional().nullable(),
  organizationState: z.string().optional().nullable(),
  organizationCity: z.string().optional().nullable(),
  organizationCountry: z.string().optional().nullable(),
  organizationLogo: z.string().optional().nullable(),

  // Relation Details (supplier)
  relationId: z.string().min(1, { message: "Relation is required" }),
  relationName: z.string(),
  relationAddressLine1: z.string().optional().nullable(),
  relationAddressLine2: z.string().optional().nullable(),
  relationZipcode: z.string().optional().nullable(),
  relationState: z.string().optional().nullable(),
  relationCity: z.string().optional().nullable(),
  relationCountry: z.string().optional().nullable(),

  // Financial Fields
  currency: z.string(),
  exchangeRates: z.record(z.string(), z.number()).nullable(),
  date: z.date(),
  subtotal: z.number(),
  taxAmount: z.number().optional(),
  discount: z.number().optional(),
  total: z.number(),

  // Expense-specific fields
  expenseNumber: z.string().min(1, { message: "Expense number is required" }),
  status: expenseStatusEnum.optional(),
  dueDate: z.date().optional().nullable(),
  attachmentUrl: z.string().optional().nullable(),
  attachmentName: z.string().optional().nullable(),
  note: z.string().optional().nullable(),
  organizationBankAccountId: z.string().optional().nullable(),
  organizationBankAccountName: z.string().optional().nullable(),
  organizationBankAccountCountry: z.string().optional().nullable(),
  organizationBankAccountCurrency: z.string().optional().nullable(),
  organizationBankAccountIban: z.string().optional().nullable(),
  relationBankAccountId: z.string().optional().nullable(),
  relationBankAccountName: z.string().optional().nullable(),
  relationBankAccountCountry: z.string().optional().nullable(),
  relationBankAccountCurrency: z.string().optional().nullable(),
  relationBankAccountIban: z.string().optional().nullable(),
  projectId: z.string().optional().nullable(),

  // Category
  categoryId: z.string().uuid().optional().nullable(),

  //Payment fields
  isPaid: z.boolean().optional().nullable(),
  paymentDate: z.date().optional().nullable(),
  paymentMethod: paymentMethodEnum.optional().nullable(),
  paymentReference: z.string().optional().nullable(),
});

export const createExpenseSchema = baseExpenseSchema;

export const editExpenseSchema = baseExpenseSchema.extend({
  id: z.string(),
});

export const getExpenseSchema = z.object({
  id: z.string(),
});

export const updateExpenseStatusSchema = z.object({
  status: expenseStatusEnum,
});
export const getExpensesParamsSchema = z.object({
  relationId: z.string().optional(),
  projectId: z.string().optional(),
  isConfirmed: z.union([z.boolean(), z.string().transform((val) => val === "true")]).optional(),
});

export type ZodCreateExpenseSchema = z.infer<typeof createExpenseSchema>;
export type ZodEditExpenseSchema = z.infer<typeof editExpenseSchema>;
export type ZodGetExpensesParams = z.infer<typeof getExpensesParamsSchema>;

export const createExpenseSchemaDefaultValues: ZodCreateExpenseSchema = {
  status: "draft",

  organizationName: "",
  organizationLogo: "",
  organizationAddressLine1: "",
  organizationAddressLine2: "",
  organizationCity: "",
  organizationState: "",
  organizationZipcode: "",
  organizationCountry: "",

  relationId: "",
  relationName: "",
  relationAddressLine1: "",
  relationAddressLine2: "",
  relationCity: "",
  relationState: "",
  relationZipcode: "",
  relationCountry: "",

  currency: "USD",
  exchangeRates: null,
  expenseNumber: "",
  date: new Date(),
  dueDate: null,
  total: 0,
  subtotal: 0,
  taxAmount: 0,
  discount: 0,

  attachmentUrl: null,
  attachmentName: null,
  note: null,

  organizationBankAccountId: undefined,
  organizationBankAccountName: undefined,
  organizationBankAccountCountry: undefined,
  organizationBankAccountCurrency: undefined,
  organizationBankAccountIban: undefined,

  relationBankAccountId: undefined,
  relationBankAccountName: undefined,
  relationBankAccountCountry: undefined,
  relationBankAccountCurrency: undefined,
  relationBankAccountIban: undefined,

  categoryId: null,

  isPaid: true,
  paymentDate: new Date(),
  paymentMethod: null,
  paymentReference: null,
};

export const EditExpensePageSchema = z.object({
  id: z.string(),
});

export const analysisStatusEnum = z.enum(["none", "pending", "completed", "failed"]);
export type AnalysisStatusType = z.infer<typeof analysisStatusEnum>;

export const expenseSchema = baseExpenseSchema.extend({
  id: z.string(),
  paidAt: z.date().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  userName: z.string().optional().nullable(),
  userImage: z.string().optional().nullable(),
  totalPaid: z.number().optional(),
  // Category
  categoryName: z.string().optional().nullable(),
  categoryColor: z.string().optional().nullable(),
  // AI Analysis Fields
  analysisStatus: analysisStatusEnum.optional().nullable(),
  analysisError: z.string().optional().nullable(),
  analysisStartedAt: z.date().optional().nullable(),
  analysisCompletedAt: z.date().optional().nullable(),
  extractedData: z.any().optional().nullable(),
  isConfirmed: z.boolean().default(true),
  // Email ingestion provenance
  source: z.enum(["upload", "email"]).optional().nullable(),
  sourceSender: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ExpenseSchema = z.infer<typeof expenseSchema>;

export const extractDataFromExpenseSchema = z.object({
  // Organization Details (simplified for extraction)
  organizationBankAccountIban: z.string().optional().nullable(),

  // Relation/Supplier Details (with extra fields for extraction)
  relationName: z.string().optional().nullable(),
  relationAddressLine1: z.string().optional().nullable(),
  relationAddressLine2: z.string().optional().nullable(),
  relationZipcode: z.string().optional().nullable(),
  relationState: z.string().optional().nullable(),
  relationCity: z.string().optional().nullable(),
  relationCountry: z.string().optional().nullable(),
  relationTaxNumber: z.string().optional().nullable(),
  relationEmail: z.string().optional().nullable(),
  relationBankAccountIban: z.string().optional().nullable(),

  // Document Details
  expenseNumber: z.string().optional().nullable(),
  currency: z.string().optional().nullable(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .optional()
    .nullable(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Due date must be in YYYY-MM-DD format")
    .optional()
    .nullable(),
  exchangeRate: z.number().optional().nullable(),

  // Totals
  subtotal: z.number().optional().nullable(),
  taxAmount: z.number().optional().nullable(),
  discount: z.number().optional().nullable(),
  total: z.number().optional().nullable(),

  // Items (kept for backwards compatibility but always empty)
  items: z.array(lineItemSchema),

  // Payment Information
  isPaid: z.boolean().optional().nullable(),
  paymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Payment date must be in YYYY-MM-DD format")
    .optional()
    .nullable(),
  paymentMethod: paymentMethodEnum.optional().nullable(),
  paymentReference: z.string().optional().nullable(),
  paymentIBAN: z.string().optional().nullable(),
  note: z.string().optional().nullable(),

  // Category (AI-suggested)
  categoryId: z.string().uuid().optional().nullable(),
  categoryName: z.string().optional().nullable(),
  isNewCategorySuggestion: z.boolean().optional().nullable(),

  // Error
  error: z.string().optional().nullable(),
});

export type ExtractDataFromExpense = z.infer<typeof extractDataFromExpenseSchema>;
